/**
 * Timezone Utilities for Hotel PMS
 * Automatically detects and uses user's local timezone
 */

/**
 * Get user's local timezone
 * @returns {string} IANA timezone identifier (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get current date in user's local timezone (YYYY-MM-DD format)
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getTodayLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get current time in user's local timezone
 * @returns {Date} Current date/time
 */
export const getNowLocal = () => {
  return new Date();
};

/**
 * Format date to local date string (YYYY-MM-DD)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateLocal = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format date and time for display
 * @param {Date|string} date - Date object or ISO string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date/time string
 */
export const formatDateTimeLocal = (date, options = {}) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(d);
};

/**
 * Format time only
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted time string (e.g., "2:30 PM")
 */
export const formatTimeLocal = (date) => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(d);
};

/**
 * Get date input value (for <input type="date">)
 * Ensures the date is in YYYY-MM-DD format for the user's timezone
 * @param {Date|string|null} date - Date to format, or null for today
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getDateInputValue = (date = null) => {
  if (!date) return getTodayLocal();
  return formatDateLocal(date);
};

/**
 * Add days to a date in local timezone
 * @param {Date|string} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {string} New date in YYYY-MM-DD format
 */
export const addDaysLocal = (date, days) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const newDate = new Date(d);
  newDate.setDate(newDate.getDate() + days);
  return formatDateLocal(newDate);
};

/**
 * Get time of day (for shift determination)
 * @returns {string} 'morning' | 'afternoon' | 'evening' | 'night'
 */
export const getTimeOfDay = () => {
  const hour = new Date().getHours();
  
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

/**
 * Get shift based on current time
 * @returns {string} 'morning' | 'afternoon' | 'night'
 */
export const getCurrentShiftTime = () => {
  const hour = new Date().getHours();
  
  if (hour >= 6 && hour < 14) return 'morning';
  if (hour >= 14 && hour < 22) return 'afternoon';
  return 'night';
};

/**
 * Check if date is today
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

/**
 * Check if date is tomorrow
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if date is tomorrow
 */
export const isTomorrow = (date) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return d.toDateString() === tomorrow.toDateString();
};

/**
 * Get relative date string
 * @param {Date|string} date - Date to format
 * @returns {string} Relative date string (e.g., "Today", "Tomorrow", "Jan 15")
 */
export const getRelativeDateString = (date) => {
  if (!date) return '';
  
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  
  return formatDateTimeLocal(date, {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Calculate number of days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of days
 */
export const daysBetween = (startDate, endDate) => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Get timezone offset string
 * @returns {string} Timezone offset (e.g., "UTC-5", "UTC+5:30")
 */
export const getTimezoneOffset = () => {
  const offset = -(new Date().getTimezoneOffset());
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const sign = offset >= 0 ? '+' : '-';
  
  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Get full timezone info for display
 * @returns {object} Timezone information
 */
export const getTimezoneInfo = () => {
  return {
    timezone: getUserTimezone(),
    offset: getTimezoneOffset(),
    displayName: new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'long'
    }).format(new Date()).split(', ')[1] || getUserTimezone()
  };
};

export default {
  getUserTimezone,
  getTodayLocal,
  getNowLocal,
  formatDateLocal,
  formatDateTimeLocal,
  formatTimeLocal,
  getDateInputValue,
  addDaysLocal,
  getTimeOfDay,
  getCurrentShiftTime,
  isToday,
  isTomorrow,
  getRelativeDateString,
  daysBetween,
  getTimezoneOffset,
  getTimezoneInfo
};
